/* @flow */

import type { ProductCardProduct } from "shop-state/types";

import React, { useContext } from "react";
import { useData } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import { Link } from "react-router-dom";
import { HomeData } from "data";
import { StoreInfoContext } from "entrypoint/shared";
import { Helmet } from "react-helmet-async";
import { formatMeta } from "helpers/get-meta";
import Hero from "components/Hero";
import ButtonLink from "components/ButtonLink";
import LoadingView from "components/LoadingView";
import ProductPreview from "components/ProductPreview";
import Carousel from "components/Carousel";
import ProductCard from "components/ProductCard";
import { useBreakpoints } from "helpers/use-breakpoints";
import useBrowserDimensions from "helpers/use-browser-dimensions";

import styles from "./styles.scss";

type CategoryCarouselProps = {
  category: {
    name: string,
    url: string,
    products: {
      items: Array<ProductCardProduct>,
    },
  },
};

const HomeView = (): React$Node => {
  const home = useData(HomeData);
  const { getNumberOfDisplayedItems } = useBreakpoints(styles);
  const {
    categories,
    info: { defaultTitle, defaultDescription },
  } = useContext(StoreInfoContext);

  if (home.state !== "LOADED") {
    return <LoadingView />;
  }

  const meta = formatMeta({
    title: defaultTitle,
    description: defaultDescription,
  });

  const getAmountOfDisplayedCategories = () => {
    const breakpointColumnMapping = {
      medium: 2,
      large: 3,
    };
    return getNumberOfDisplayedItems(breakpointColumnMapping, 3);
  };

  const CategoryCarousel = ({ category }: CategoryCarouselProps) => {
    const t = useTranslate();
    const { width: browserWidth } = useBrowserDimensions();
    const small = browserWidth <= Number.parseInt(styles.small, 10);

    const breadcrumbLinks = [{
      pathname: category.url,
      title: category.name,
      hint: {
        name: category.name,
      },
    }];

    return (
      <section className={styles.section}>
        <header className={styles.header}>
          <h2>{category.name}</h2>
          <ButtonLink to={category.url} size={small ? "small" : "medium"} variant="dark">{t("HOMEVIEW.GO_TO")}</ButtonLink>
        </header>

        <Carousel
          className={styles.carousel}
          slidesToShow={getAmountOfDisplayedCategories()}
          items={category.products.items
            .slice(0, getAmountOfDisplayedCategories()).map((p, i) => (
              <div key={p.sku} className={styles.card}>
                <ProductCard
                  product={p}
                  position={i}
                  breadcrumbLinks={[...breadcrumbLinks]}
                  list={category.name}
                />
              </div>
            ))
          }
        />
      </section>
    );
  };

  return (
    <div className={styles.block}>
      <Helmet title={meta.title} meta={meta.data} />

      <h1 className="sr-only">{meta.title}</h1>

      {home.data.hero && (
        <Hero block={home.data.hero.block} buttonModifier="primary" />
      )}

      <div className={styles.content}>
        <div className={styles.categoriesWrapper}>
          <div className={styles.categories}>
            {categories.map(x => (
              <div key={x.url} className={styles.col}>
                <Link className={styles.category} to={x.url}>
                  <img src={x.image.src} alt="" />
                  <h3>{x.name}</h3>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {home.data.productPreview && (
          <ProductPreview product={home.data.productPreview.product} />
        )}
        {home.data.popularProducts && (
          <CategoryCarousel category={home.data.popularProducts.category} />
        )}
        {home.data.categoryPreview1 && (
          <CategoryCarousel category={home.data.categoryPreview1.category} />
        )}
        {home.data.categoryPreview2 && (
          <CategoryCarousel category={home.data.categoryPreview2.category} />
        )}

      </div>
    </div>
  );
};

export default HomeView;
